import {useQueries} from '@github-ui/react-query'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import {rowNodeKey, rowNodeQueryKey} from './issues-dashboard-row-node'

type OverlayRow = {
  itemType: 'issue' | 'pull_request'
  id: number | bigint | string
  globalId?: string
  aliveChannel?: string
}

/**
 * Per-row live-update overlay, shared by the dashboard and repo Issues lists.
 * Each row with a signed `aliveChannel` gets its own query seeded from the list;
 * a live update refreshes just that row via `_rows`. The row's own copy wins only
 * when it's newer than the list, so a list refetch (filter/sort/page) stays fresh.
 */
export function useRowNodeOverlay<T extends OverlayRow>({
  rows,
  listUpdatedAt,
  loadRowNode,
}: {
  rows: T[]
  listUpdatedAt: number
  loadRowNode: (globalId: string) => Promise<T | undefined>
}): T[] {
  const nodeResults = useQueries({
    queries: rows.map(item => ({
      queryKey: rowNodeQueryKey(rowNodeKey(item)),
      queryFn: async (): Promise<T> => {
        // Live-update rows always carry a resolvable global identity (the
        // backend emits it alongside the aliveChannel); fetch the fresh row by
        // that identity.
        if (item.aliveChannel) {
          return (await loadRowNode(getListItemIdentity(item))) ?? item
        }
        return item
      },
      enabled: !!item.aliveChannel,
      initialData: item,
      initialDataUpdatedAt: listUpdatedAt,
      // Never refetch on mount/focus/interval — only on explicit invalidation
      // from a live update (invalidation overrides staleTime and forces a refetch).
      staleTime: Number.POSITIVE_INFINITY,
    })),
  })

  return rows.map((item, index) => {
    const node = nodeResults[index]
    return node?.data && node.dataUpdatedAt > listUpdatedAt ? node.data : item
  })
}
